"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "@/stores/session";
import { User } from "@/lib/types/api";

export function useSessionSync() {
  const pathname = usePathname();
  const { setUser, clearUser, setLoading } = useSession();
  const previousPathnameRef = useRef<string | null>(null);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    const check = async () => {
      // İlk mount'ta hemen kontrol et
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false;
        setLoading(true);
        try {
          const res = await fetch("/api/me", { 
            cache: "no-store",
            credentials: "include"
          });

          if (res.status === 200) {
            const data = await res.json();
            if (data.user) {
              setUser(data.user as User);
            } else {
              clearUser();
            }
          } else if (res.status === 401) {
            clearUser();
          } else {
            if (process.env.NODE_ENV !== 'production') {
              console.warn(`Session check failed with status: ${res.status}`);
            }
            clearUser();
          }
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Session check network error:', error);
          }
          clearUser();
        } finally {
          setLoading(false);
        }
        previousPathnameRef.current = pathname;
        return;
      }

      // Pathname değiştiyse, sayfa yüklenmesini bekle
      if (previousPathnameRef.current !== pathname) {
        previousPathnameRef.current = pathname;

        // Sayfa geçişi oldu, sayfanın tamamen yüklenmesini bekle
        const handlePageLoad = async () => {
          // NextTopLoader'ın tamamlanması için kısa bir gecikme
          // Ayrıca sayfa render'ının tamamlanması için next tick bekleyelim
          await new Promise(resolve => {
            // window load event'i zaten geçtiyse hemen devam et
            if (document.readyState === 'complete') {
              // Next.js'in hydration ve render işlemlerinin tamamlanması için kısa bir delay
              setTimeout(resolve, 100);
            } else {
              // Sayfa hala yükleniyorsa load event'ini bekle
              window.addEventListener('load', () => {
                setTimeout(resolve, 100);
              }, { once: true });
            }
          });

          setLoading(true);
          try {
            const res = await fetch("/api/me", { 
              cache: "no-store",
              credentials: "include"
            });

            if (res.status === 200) {
              const data = await res.json();
              if (data.user) {
                setUser(data.user as User);
              } else {
                clearUser();
              }
            } else if (res.status === 401) {
              clearUser();
            } else {
              if (process.env.NODE_ENV !== 'production') {
                console.warn(`Session check failed with status: ${res.status}`);
              }
              clearUser();
            }
          } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
              console.warn('Session check network error:', error);
            }
            clearUser();
          } finally {
            setLoading(false);
          }
        };

        handlePageLoad();
      }
    };

    check();
  }, [pathname, setUser, clearUser, setLoading]);
}

